'use client';

/**
 * Edge-docked support control.
 *
 * Renders only for authenticated users (Freshworks was previously rendered
 * site-wide regardless of auth state — for an unauthenticated user the
 * support pathway is the `mailto:support@foltrigg.ai` link on `/login`,
 * which does not require any client-side identity).
 *
 * At rest it is a small collapsed circular "?" handle tucked against the
 * lower-right edge, so it never overlaps page content or the transcript
 * playback UI (issue #534). On hover/focus it slides out and expands into the
 * full "Support" tab; clicking it opens the dialog. Tailwind classes only —
 * matches the webapp's "no inline styles" convention (`CLAUDE.md` Web UI
 * section).
 */

import { useState } from 'react';
import { QuestionMarkCircledIcon } from '@radix-ui/react-icons';
import { useAuth } from '@/lib/auth-context';
import { SupportDialog } from './SupportDialog';

export function SupportButton() {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);

  if (loading || !user) {
    return null;
  }

  return (
    <>
      <div className="group fixed bottom-8 right-0 z-40">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Contact support"
          title="Contact support"
          className={[
            // Layout — collapsed circle at rest (pl/pr equal → ~h-11 circle)
            'flex h-11 items-center overflow-hidden rounded-l-full pl-3 pr-3 group-hover:pr-4',
            // Color + depth
            'bg-orange-9 text-white hover:bg-orange-10',
            'ring-1 ring-inset ring-white/25 shadow-lg shadow-orange-9/30',
            'hover:shadow-xl hover:shadow-orange-9/40',
            // Tucked to the side at rest; slides flush on hover/focus
            'translate-x-[18%] group-hover:translate-x-0 group-focus-within:translate-x-0',
            // Motion (respect reduced-motion preferences)
            'transition-[transform,box-shadow,background-color,padding] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
            'motion-reduce:transition-none',
            // Focus ring
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-7',
          ].join(' ')}
        >
          <QuestionMarkCircledIcon className="h-5 w-5 shrink-0" />
          <span
            className={[
              'max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold tracking-tight opacity-0',
              'transition-[max-width,opacity,margin] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
              'motion-reduce:transition-none',
              'group-hover:ml-2 group-hover:max-w-24 group-hover:opacity-100',
              'group-focus-within:ml-2 group-focus-within:max-w-24 group-focus-within:opacity-100',
            ].join(' ')}
          >
            Support
          </span>
        </button>
      </div>
      <SupportDialog isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
}
